import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useChannels } from "api/channels";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasViewAccessToResource } from "api/general";
import { FormButtons } from "components/FormControls/FormButtons";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import { BackendChannelModel, ChannelFormData } from "types/channels";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./ChannelForm.styles";

export function ChannelForm(props: {
  editMode?: boolean;
  initialValues?: BackendChannelModel;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { addChannel, editChannel } = useChannels(
    props.initialValues?.channelId,
  );
  const hasViewChannelPermission = hasViewAccessToResource("CHANNEL");

  const initialValues: ChannelFormData =
    props.editMode && props.initialValues
      ? {
          name: props.initialValues.name,
          identifier: props.initialValues.identifier,
          description: props.initialValues.description,
        }
      : {
          name: "",
          identifier: "",
          description: "",
        };

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});
        try {
          props.editMode && props.initialValues
            ? await editChannel(values)
            : await addChannel(values);
          navigate(links.channelList);
          notify(
            message({
              id: props.editMode ? "channelUpdated" : "channelAdded",
            }),
          );
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          setStatus(errors);
          focusError();
        }
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        values,
        dirty,
        initialValues,
      }) => {
        const formError = status?.[""];
        return (
          <Form onSubmit={handleSubmit}>
            <FormSection
              sectionError={formError}
              subTitle={
                <Typography className={classes.sectionSubheader} mb={8}>
                  {message({ id: "basicSettingsInfo" })}
                </Typography>
              }
              title={message({ id: "basicSettings" })}
            >
              <Grid container spacing={12}>
                <Grid xs={6} item>
                  <TextInput
                    data-test-id="channel-name-input"
                    disabled={hasViewChannelPermission}
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "channelName" })}
                      />
                    }
                    name="name"
                    width="100%"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-test-id="channel-identifier-input"
                    disabled={hasViewChannelPermission}
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "channelCode" })}
                      />
                    }
                    name="identifier"
                    width="100%"
                  />
                </Grid>
                <Grid xs={12} item>
                  <TextInput
                    data-test-id="channel-description-input"
                    disabled={hasViewChannelPermission}
                    label={message({ id: "channelDescription" })}
                    name="description"
                    width="100%"
                  />
                </Grid>
              </Grid>
            </FormSection>
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting || hasViewChannelPermission}
              onCancelClick={() => navigate(links.channelList)}
              onSubmitClick={() => handleSubmit()}
              {...(!props.editMode && { submitMessage: "addChannel" })}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
